'use client'

import { styled } from '@rybr/styled-modules'
import React, { ComponentProps, CSSProperties, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

export type SanityImageProps = ComponentProps<typeof SanityImage>

const Img = styled.img<{ isBlurred: boolean; width: number; height: number }>(
  'SanityImage',
  ({ width, height, isBlurred }) => ({
    filter: isBlurred ? `blur(5px)` : `blur(0px)`,
    width: width == null ? 'auto' : `${width}px`,
    height: height == null ? 'auto' : `${height}px`
  })
)

export const SanityImage: React.FC<{
  lqip?: string | null
  src: string | undefined
  srcSet?: string
  sizes?: string
  className?: string
  alt: string
  height: number
  width: number
}> = ({ src, srcSet, lqip, sizes, ...props }) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const hasLoaded = useRef(false)
  const imageObserver = useRef<IntersectionObserver>(null)
  const [isBlurred, setIsBlurred] = useState(() => !!lqip)
  const [imgSrc, setImgSrc] = useState<{ src?: string; srcSet?: string; sizes?: string }>(() =>
    lqip
      ? {
          src: lqip
        }
      : {}
  )

  useLayoutEffect(() => {
    if (hasLoaded.current && (src !== imgSrc.src || srcSet !== imgSrc.srcSet || sizes !== imgSrc.sizes)) {
      setImgSrc({
        src,
        srcSet,
        sizes
      })
    } else if (!hasLoaded.current && imageRef.current) {
      imageObserver.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              hasLoaded.current = true

              setImgSrc({
                src,
                srcSet,
                sizes
              })

              imageObserver.current?.disconnect()
            }
          })
        },
        {
          root: null,
          rootMargin: '50%'
        }
      )

      imageObserver.current.observe(imageRef.current)
    }

    return () => {
      imageObserver.current?.disconnect()
      imageObserver.current = null
    }
  }, [src, srcSet, sizes])

  const overrideStyle = useMemo(
    () =>
      isBlurred
        ? ({
            aspectRatio: props.height ? props.width / props.height : 'auto'
          } satisfies CSSProperties)
        : {},
    [isBlurred, props.width, props.height]
  )

  const onLoad = useCallback(() => {
    if (hasLoaded.current) {
      setIsBlurred(false)
    }
  }, [setIsBlurred])

  return !src && !srcSet ? null : (
    <Img
      {...props}
      {...imgSrc}
      onLoad={onLoad}
      style={overrideStyle}
      ref={imageRef}
      $isBlurred={!hasLoaded.current && isBlurred}
      $width={props.width}
      $height={props.height}
    />
  )
}
